import request from '@/api/request'

export interface ExpenseClaimItem {
  itemDate: string
  amount: number
  description?: string
  invoiceImage?: string
  ocrData?: any
}

export interface CreateExpenseClaimData {
  employeeId: number
  categoryId: number
  accountId?: number
  items: ExpenseClaimItem[]
  totalAmount: number
  remark?: string
  relatedOrderId?: number
  relatedCustomerId?: number
  refType?: 'sales_order' | 'customer' | 'purchase_order' | ''
  refId?: number
}

export interface QueryExpenseClaimParams {
  status?: string
  employeeId?: number
  accountId?: number
  categoryId?: number
  logisticsCompanyId?: number
  keyword?: string
  startDate?: string
  endDate?: string
  page?: number
  pageSize?: number
}

export interface ExpenseCategory {
  id: number
  name: string
  code?: string
  parentId?: number | null
  level?: number
  sortOrder?: number
  isActive?: boolean
  children?: ExpenseCategory[]
  createdAt?: string
  updatedAt?: string
}

// 获取报销单列表
export function getExpenseClaimList(params: QueryExpenseClaimParams) {
  return request({
    url: '/expense-claims',
    method: 'get',
    params
  }).then(res => res.data)
}

// 获取报销单详情
export function getExpenseClaimDetail(id: number) {
  return request({
    url: `/expense-claims/${id}`,
    method: 'get'
  }).then(res => res.data)
}

// 创建报销单
export function createExpenseClaim(data: CreateExpenseClaimData) {
  return request({
    url: '/expense-claims',
    method: 'post',
    data
  }).then(res => res.data)
}

// 更新报销单
export function updateExpenseClaim(id: number, data: Partial<CreateExpenseClaimData>) {
  return request({
    url: `/expense-claims/${id}`,
    method: 'put',
    data
  }).then(res => res.data)
}

// 提交审核
export function submitExpenseClaim(id: number) {
  return request({
    url: `/expense-claims/${id}/submit`,
    method: 'post'
  }).then(res => res.data)
}

// 财务一审
export function financeReviewExpenseClaim(id: number) {
  return request({
    url: `/expense-claims/${id}/finance-review`,
    method: 'post'
  }).then(res => res.data)
}

// 出纳二审
export function cashierReviewExpenseClaim(id: number) {
  return request({
    url: `/expense-claims/${id}/cashier-review`,
    method: 'post'
  }).then(res => res.data)
}

// 驳回报销单
export function rejectExpenseClaim(id: number, data: { reason: string }) {
  return request({
    url: `/expense-claims/${id}/reject`,
    method: 'post',
    data
  }).then(res => res.data)
}

// 执行付款（必须上传付款凭证；fee 手续费可选，额外从付款账户扣除；paymentDate 可选，补录按真实付款日记流水）
export function payExpenseClaim(id: number, data: { paymentVouchers: string[]; fee?: number; paymentDate?: string }) {
  return request({
    url: `/expense-claims/${id}/pay`,
    method: 'post',
    data
  }).then(res => res.data)
}

// 确认收款（报销人本人或 admin，paid → completed）
export function confirmReceiptExpenseClaim(id: number) {
  return request({
    url: `/expense-claims/${id}/confirm-receipt`,
    method: 'post'
  }).then(res => res.data)
}

// 管理员回退一步（completed→paid→cashier_reviewed→finance_reviewed→pending）
export function rollbackExpenseClaim(id: number, data: { reason: string }) {
  return request({
    url: `/expense-claims/${id}/rollback`,
    method: 'post',
    data
  }).then(res => res.data)
}

// 批量操作结果明细（仅 admin 可用的批量端点返回）
export interface BatchResultItem {
  id: number
  claimNo: string | null
  ok: boolean
  message: string
}

export interface BatchResult {
  successCount: number
  failCount: number
  results: BatchResultItem[]
}

// 批量财务一审（仅 admin）
export function batchFinanceReview(ids: number[]) {
  return request({
    url: '/expense-claims/batch-finance-review',
    method: 'post',
    data: { ids }
  }).then(res => res.data)
}

// 批量出纳二审（仅 admin）
export function batchCashierReview(ids: number[]) {
  return request({
    url: '/expense-claims/batch-cashier-review',
    method: 'post',
    data: { ids }
  }).then(res => res.data)
}

// 批量付款（仅 admin，凭证共享给整批；fee 手续费可选，整批每笔各自扣该手续费；paymentDate 可选，共享给整批）
export function batchPay(ids: number[], voucherImages: string[], fee?: number, paymentDate?: string, expectedAmount?: number) {
  return request({
    url: '/expense-claims/batch-pay',
    method: 'post',
    data: { ids, voucherImages, fee, paymentDate, expectedAmount }
  }).then(res => res.data)
}

// 批量确认收款（仅 admin）
export function batchConfirmReceipt(ids: number[]) {
  return request({
    url: '/expense-claims/batch-confirm-receipt',
    method: 'post',
    data: { ids }
  }).then(res => res.data)
}

// 删除报销单
export function deleteExpenseClaim(id: number) {
  return request({
    url: `/expense-claims/${id}`,
    method: 'delete'
  }).then(res => res.data)
}

// 获取报销统计（跟随列表筛选，忽略 status 本身）
export function getExpenseClaimStats(params?: Omit<QueryExpenseClaimParams, 'status' | 'page' | 'pageSize'> & { keyword?: string }) {
  return request({
    url: '/expense-claims/stats',
    method: 'get',
    params
  }).then(res => res.data)
}

// 获取待财务一审列表
export function getPendingFinanceList() {
  return request({
    url: '/expense-claims/pending-finance',
    method: 'get'
  }).then(res => res.data)
}

// 获取待出纳二审列表
export function getPendingCashierList() {
  return request({
    url: '/expense-claims/pending-cashier',
    method: 'get'
  }).then(res => res.data)
}

// 获取报销类型列表
export function getExpenseCategories(flat?: boolean) {
  return request({
    url: '/expense-claims/categories',
    method: 'get',
    params: flat ? { flat: '1' } : undefined
  }).then(res => res.data)
}

// 创建报销类型
export function createExpenseCategory(data: { name: string; code?: string; parentId?: number | null; sortOrder?: number; isActive?: boolean }) {
  return request({
    url: '/expense-claims/categories',
    method: 'post',
    data
  }).then(res => res.data)
}

// 更新报销类型
export function updateExpenseCategory(id: number, data: { name: string; code?: string; parentId?: number | null; sortOrder?: number; isActive?: boolean }) {
  return request({
    url: `/expense-claims/categories/${id}`,
    method: 'put',
    data
  }).then(res => res.data)
}

// 删除报销类型
export function deleteExpenseCategory(id: number) {
  return request({
    url: `/expense-claims/categories/${id}`,
    method: 'delete'
  }).then(res => res.data)
}

// 订单关联报销（销售单详情"关联报销"区块）
export function getExpensesByOrder(orderId: number) {
  return request({
    url: `/expense-claims/by-order/${orderId}`,
    method: 'get'
  }).then(res => res.data)
}

// 采购单关联报销（采购单详情"关联报销"区块）
export function getExpensesByPurchaseOrder(orderId: number) {
  return request({
    url: `/expense-claims/by-purchase-order/${orderId}`,
    method: 'get'
  }).then(res => res.data)
}

// 客户关联报销
export function getExpensesByCustomer(customerId: number) {
  return request({
    url: `/expense-claims/by-customer/${customerId}`,
    method: 'get'
  }).then(res => res.data)
}

// 订单净利润（金额-成本-关联费用）
export function getOrderProfit(orderId: number) {
  return request({
    url: `/expense-claims/profit/${orderId}`,
    method: 'get'
  }).then(res => res.data)
}

// 客户利润贡献（订单总额-关联费用）
export function getCustomerProfit(customerId: number) {
  return request({
    url: `/expense-claims/customer-profit/${customerId}`,
    method: 'get'
  }).then(res => res.data)
}
